<script>
import { mapActions } from 'vuex';
import StatusDropdown from './statusDropdown.vue';
import AoikumoSyncBadge from 'dashboard/components/dahreply/AoikumoSyncBadge.vue';

export default {
  components: {
    StatusDropdown,
    AoikumoSyncBadge,
  },
  props: {
    custom_metadata: {
      type: Object,
      default: null,
    },
    conversation_id: {
      type: Number,
      default: null,
    },
    customer_id: {
      type: Number,
      default: null,
    },
    date: {
      type: String,
      default: () => this.$t('APPOINTMENTS.APPOINTMENT_LIST.DATE'),
    },
    start_time: {
      type: String,
      default: () => this.$t('APPOINTMENTS.APPOINTMENT_LIST.START_TIME'),
    },
    end_time: {
      type: String,
      default: () => this.$t('APPOINTMENTS.APPOINTMENT_LIST.END_TIME'),
    },
    service: {
      type: Object,
      default: () => ({ service_name: this.$t('APPOINTMENTS.APPOINTMENT_LIST.NOT_SPECIFIED') }),
    },
    staff: {
      type: Object,
      default: () => ({}),
    },
    status: {
      type: [String, Number],
      default: () => this.$t('APPOINTMENTS.APPOINTMENT_LIST.STATUS'),
    },
    contact: {
      type: Object,
      default: () => ({
        name: this.$t('APPOINTMENTS.APPOINTMENT_LIST.UNKNOWN'),
        phone_number: '',
        email: ''
      }),
    },
    before_reschedule_metadata: {
      type: Object,
      default: null,
    },
    branch: {
      type: Object,
      default: () => ({ branch_name: this.$t('APPOINTMENTS.APPOINTMENT_LIST.NOT_SPECIFIED') }),
    },
    selected: {
      type: Boolean,
      default: false,
    },
    appointment_id: {
      type: [Number, String],
      default: null,
    },
    reminders: {
      type: Array,
      default: () => [24, 1, 0.25] // 24 hours, 1 hour, and 15 minutes
    }
  },
  methods: {
    ...mapActions('appointments', ['fetchBranch']),
    handleKeyDown(event) {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        this.$emit('click');
      }
    },
    formatDateWithDay(dateString) {
      if (!dateString || dateString === this.$t('APPOINTMENTS.APPOINTMENT_LIST.DATE')) return dateString;

      try {
        const date = new Date(dateString);
        const days = [
          this.$t('APPOINTMENTS.APPOINTMENT_LIST.SUNDAY'),
          this.$t('APPOINTMENTS.APPOINTMENT_LIST.MONDAY'),
          this.$t('APPOINTMENTS.APPOINTMENT_LIST.TUESDAY'),
          this.$t('APPOINTMENTS.APPOINTMENT_LIST.WEDNESDAY'),
          this.$t('APPOINTMENTS.APPOINTMENT_LIST.THURSDAY'),
          this.$t('APPOINTMENTS.APPOINTMENT_LIST.FRIDAY'),
          this.$t('APPOINTMENTS.APPOINTMENT_LIST.SATURDAY'),
        ];
        const dayName = days[date.getDay()];
        return `${dateString}, ${dayName}`;
      } catch (e) {
        return dateString;
      }
    },
    formatTime(timeString) {
      if (!timeString || timeString === this.$t('APPOINTMENTS.APPOINTMENT_LIST.START_TIME') || timeString === this.$t('APPOINTMENTS.APPOINTMENT_LIST.END_TIME')) return timeString;

      try {
        const timeParts = timeString.split(':');
        let hours = parseInt(timeParts[0], 10);
        const minutes = timeParts[1];
        const ampm = hours >= 12 ? this.$t('APPOINTMENTS.APPOINTMENT_LIST.PM') : this.$t('APPOINTMENTS.APPOINTMENT_LIST.AM');

        hours = hours % 12;
        hours = hours ? hours : 12;

        return `${hours}:${minutes} ${ampm}`;
      } catch (e) {
        return timeString;
      }
    },
    formatReminderTime(hours) {
      if (hours >= 24) {
        const days = Math.floor(hours / 24);
        return this.$tc('APPOINTMENTS.APPOINTMENT_LIST.DAYS_PRIOR', days, { count: days });
      } else if (hours >= 1) {
        return this.$tc('APPOINTMENTS.APPOINTMENT_LIST.HOURS_PRIOR', hours, { count: hours });
      } else {
        const minutes = Math.round(hours * 60);
        return this.$tc('APPOINTMENTS.APPOINTMENT_LIST.MINUTES_PRIOR', minutes, { count: minutes });
      }
    }
  },
  computed: {
    accountId() {
      return this.$route.params.accountId;
    },
    isServiceDeleted() {
      return this.service?.is_deleted || false;
    },
  },
  mounted() {
    this.$el.addEventListener('keydown', this.handleKeyDown);
  },
  beforeDestroy() {
    this.$el.removeEventListener('keydown', this.handleKeyDown);
  },
}
</script>

<template>
<div
  data-testid="appointment_card"
  :data-id="appointment_id"
  :data-status="status"
  class="
    grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[repeat(3,2fr),1fr]
    gap-3 items-center p-4 sm:p-5 lg:p-6
    bg-white dark:bg-slate-800
    border border-slate-200 dark:border-slate-700
    shadow rounded-lg
    transition-colors duration-300 ease-in-out
    hover:shadow-md hover:bg-slate-50 dark:hover:bg-slate-700/70
    cursor-pointer
    focus:outline-none focus:ring-2 focus:ring-slate-400 dark:focus:ring-slate-500
  "
  :class="{
    '!bg-slate-100 dark:!bg-slate-700 !border-slate-300 dark:!border-slate-400 !ring-2 !ring-slate-300 dark:!ring-slate-400': selected
  }"
  role="button"
  tabindex="0"
  :aria-selected="selected"
>
    <!-- User details with improved styling and responsive layout -->
    <div class="flex flex-col justify-center gap-1.5 sm:col-span-2 lg:col-span-1">
      <div class="flex items-center gap-2.5">
        <div class="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-woot-50 dark:bg-woot-900/30 flex items-center justify-center flex-shrink-0">
          <fluent-icon icon="people" size="16" class="text-woot-600 dark:text-woot-400" />
        </div>
        <p data-testid="appointment_card_contact_name" class="font-semibold text-slate-900 dark:text-white truncate max-w-[calc(100%-3rem)]">{{ contact.name }}</p>
      </div>
      <div class="text-xs sm:text-sm text-slate-600 dark:text-slate-300 flex flex-wrap gap-x-2 gap-y-1 ml-10 sm:ml-12">
        <span class="inline-flex items-center gap-1 overflow-hidden">
          <fluent-icon icon="call" size="12" class="text-slate-500 dark:text-slate-400 flex-shrink-0" />
          <span class="truncate max-w-[8rem]" :title="contact.phone_number || '-'">{{ contact.phone_number || '-' }}</span>
        </span>
        <span class="inline-flex items-center gap-1 overflow-hidden">
          <fluent-icon icon="mail" size="12" class="text-slate-500 dark:text-slate-400 flex-shrink-0" />
          <span class="truncate max-w-[8rem]" :title="contact.email || '-'">{{ contact.email || '-' }}</span>
        </span>
      </div>
    </div>

    <!-- Date and Time -->
    <div v-if="!before_reschedule_metadata" class="bg-slate-50 dark:bg-slate-800/50 p-3 rounded-lg border border-slate-100 dark:border-slate-700 h-full">
      <div class="flex items-start mb-1.5">
        <fluent-icon icon="calendar" size="16" class="text-woot-600 dark:text-woot-400 mr-1.5 mt-0.5 flex-shrink-0" />
        <h4 class="font-semibold text-sm text-slate-700 dark:text-slate-300">{{ $t('APPOINTMENTS.APPOINTMENT_LIST.DATE_AND_TIME') }}</h4>
      </div>
      <p data-testid="appointment_card_date" class="text-xs sm:text-sm font-medium text-slate-700 dark:text-slate-200 truncate ml-6" :title="formatDateWithDay(date)">
        {{ formatDateWithDay(date) }}
      </p>
      <p data-testid="appointment_card_time" class="text-xs sm:text-sm text-slate-600 dark:text-slate-300 truncate ml-6" :title="`${formatTime(start_time)} - ${formatTime(end_time)}`">
        <span>{{ formatTime(start_time) }}</span> - <span>{{ formatTime(end_time) }}</span>
      </p>
    </div>

    <!-- Rescheduled information -->
    <div v-if="before_reschedule_metadata" class="text-xs sm:text-sm text-slate-700 dark:text-slate-200 space-y-2 bg-slate-50 dark:bg-slate-800/50 p-3 rounded-lg border border-slate-100 dark:border-slate-700 h-full">
      <div class="p-2 bg-woot-50 dark:bg-woot-900/20 rounded border-l-2 border-woot-400 dark:border-woot-500">
        <p class="font-semibold flex items-center gap-1 mb-0.5 text-xs sm:text-sm">
          <fluent-icon icon="arrow-sync" size="12" class="text-woot-500 flex-shrink-0" />
          {{ $t('APPOINTMENTS.APPOINTMENT_LIST.RESCHEDULED') }}
        </p>
        <p class="truncate text-xs" :title="formatDateWithDay(date)">{{ formatDateWithDay(date) }}</p>
        <p class="text-slate-600 dark:text-slate-300 truncate text-xs" :title="`${formatTime(start_time)} - ${formatTime(end_time)}`">
          {{ formatTime(start_time) }} - {{ formatTime(end_time) }}
        </p>
      </div>
      <div class="p-2 bg-slate-50 dark:bg-slate-700/30 rounded border-l-2 border-slate-400">
        <p class="font-semibold flex items-center gap-1 mb-0.5 text-xs sm:text-sm">
          <fluent-icon icon="history" size="12" class="text-slate-500 flex-shrink-0" />
          {{ $t('APPOINTMENTS.APPOINTMENT_LIST.ORIGINAL') }}
        </p>
        <p class="truncate text-xs" :title="formatDateWithDay(before_reschedule_metadata.date)">{{ formatDateWithDay(before_reschedule_metadata.date) }}</p>
        <p class="text-slate-600 dark:text-slate-300 truncate text-xs" :title="`${formatTime(before_reschedule_metadata.start_time)} - ${formatTime(before_reschedule_metadata.end_time)}`">
          {{ formatTime(before_reschedule_metadata.start_time) }} - {{ formatTime(before_reschedule_metadata.end_time) }}
        </p>
      </div>
    </div>

    <!-- Service, Branch, Staff -->
    <div class="bg-slate-50 dark:bg-slate-800/50 p-3 rounded-lg border border-slate-100 dark:border-slate-700 space-y-2 h-full">
      <div class="flex items-center gap-2">
        <fluent-icon icon="briefcase" size="14" class="text-woot-600 dark:text-woot-400 flex-shrink-0" />
        <div class="overflow-hidden flex-1">
          <p class="text-xs text-slate-500 dark:text-slate-400">{{ $t('APPOINTMENTS.APPOINTMENT_LIST.SERVICE') }}</p>
          <div class="flex items-center gap-1.5 mb-4">
            <p data-testid="appointment_card_service" class="mb-0 text-xs sm:text-sm font-medium text-slate-800 dark:text-slate-200 truncate" :title="service.service_name">{{ service.service_name }}</p>
            <fluent-icon 
              v-if="isServiceDeleted"
              v-tooltip.top="'This service has been deleted'"
              icon="warning" 
              size="14"
              class="text-amber-500 dark:text-amber-400 flex-shrink-0"
            />
          </div>
        </div>
      </div>
      <div class="flex items-center gap-2">
        <fluent-icon icon="building-bank" size="14" class="text-woot-600 dark:text-woot-400 flex-shrink-0" />
        <div class="overflow-hidden">
          <p class="text-xs text-slate-500 dark:text-slate-400">{{ $t('APPOINTMENTS.APPOINTMENT_LIST.BRANCH') }}</p>
          <p data-testid="appointment_card_branch" class="text-xs sm:text-sm font-medium text-slate-800 dark:text-slate-200 truncate" :title="branch.branch_name">{{ branch.branch_name }}</p>
        </div>
      </div>
      <div v-if="staff && staff.staff_name" class="flex items-center gap-2">
        <fluent-icon icon="person" size="14" class="text-woot-600 dark:text-woot-400 flex-shrink-0" />
        <div class="overflow-hidden">
          <p class="text-xs text-slate-500 dark:text-slate-400">{{ $t('APPOINTMENTS.APPOINTMENT_LIST.STAFF') }}</p>
          <p data-testid="appointment_card_staff" class="text-xs sm:text-sm font-medium text-slate-800 dark:text-slate-200 truncate" :title="staff.staff_name">{{ staff.staff_name }}</p>
        </div>
      </div>
    </div>

    <!-- Status & Actions with improved visual distinction and responsive layout -->
    <div class="flex flex-col sm:flex-row lg:flex-col items-start lg:items-end gap-2 lg:justify-end">
      <!-- Status badge -->
      <span 
        data-testid="appointment_card_status"
        class="px-2.5 py-1 text-xs font-medium rounded-full flex items-center gap-1 shadow-sm transition-colors" 
        :class="{
          'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300': status === 'cancelled' || status === 'archive',
          'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300': status === 'confirmed',
          'bg-woot-100 text-woot-800 dark:bg-woot-900/30 dark:text-woot-300': status === 'rescheduled',
          'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300': status === 'upcoming',
          'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300': status === 'requested',
        }"
      >
        <fluent-icon 
          :icon="{
            cancelled: 'dismiss-circle',
            confirmed: 'checkmark-circle',
            rescheduled: 'arrow-sync',
            completed: 'checkmark',
            upcoming: 'clock',
            archive: 'dismiss-circle',
            requested: 'clock'
          }[status.toLowerCase()] || 'clock'"
          size="12"
          class="flex-shrink-0"
        />
        {{ $t(`APPOINTMENTS.APPOINTMENT_LIST.STATUS_${status.toUpperCase()}`) }}
      </span>

      <!-- Action buttons -->
      <div class="flex flex-wrap gap-1.5 w-full sm:w-auto">
        <div class="flex flex-wrap sm:flex-nowrap gap-1.5 w-full sm:w-auto">
          <router-link :to="{ name: 'contact_profile_dashboard', params: { accountId, contactId: customer_id.toString() } }" class="relative group w-auto sm:w-auto">
            <woot-button 
              variant="smooth"
              color-scheme="secondary"
              size="tiny"
              class="flex items-center gap-1 w-full"
              :aria-label="$t('APPOINTMENTS.APPOINTMENT_LIST.VIEW_CONTACT')"
            >
              <fluent-icon icon="person" size="12" />
              <span class="truncate">{{ $t('APPOINTMENTS.APPOINTMENT_LIST.CONTACT') }}</span>
            </woot-button>
            <div class="absolute top-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-slate-900 dark:bg-slate-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap shadow z-10">
              {{ $t('APPOINTMENTS.APPOINTMENT_LIST.VIEW_CONTACT') }}
            </div>
          </router-link>

          <router-link 
            v-if="conversation_id" 
            :to="{ name: 'inbox_conversation', params: { accountId, conversation_id: this.conversation_id } }" 
            class="relative group w-auto sm:w-auto"
          >
            <woot-button 
              variant="smooth"
              color-scheme="primary"
              size="tiny"
              class="flex items-center gap-1 w-full"
              :aria-label="$t('APPOINTMENTS.APPOINTMENT_LIST.VIEW_CONVERSATION')"
            >
              <fluent-icon icon="chat" size="12" />
              <span class="truncate">{{ $t('APPOINTMENTS.APPOINTMENT_LIST.MESSAGE') }}</span>
            </woot-button>
            <div class="absolute top-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-slate-900 dark:bg-slate-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap shadow z-10">
              {{ $t('APPOINTMENTS.APPOINTMENT_LIST.VIEW_CONVERSATION') }}
            </div>
          </router-link>

          <span v-else class="relative group w-auto sm:w-auto">
            <woot-button 
              variant="smooth"
              color-scheme="primary"
              size="tiny"
              class="flex items-center gap-1 opacity-50 cursor-not-allowed w-full"
              disabled
              :aria-label="$t('APPOINTMENTS.APPOINTMENT_LIST.NO_CONVERSATION')"
            >
              <fluent-icon icon="chat" size="12" />
              <span class="truncate">{{ $t('APPOINTMENTS.APPOINTMENT_LIST.MESSAGE') }}</span>
            </woot-button>
            <div class="absolute top-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-slate-900 dark:bg-slate-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap shadow z-10">
              {{ $t('APPOINTMENTS.APPOINTMENT_LIST.NO_CONVERSATION') }}
            </div>
          </span>
        </div>
      </div>
    </div>

    <!-- Full-width so the long reason text is not squeezed into the status column -->
    <div v-if="custom_metadata && custom_metadata.aoikumo_sync" class="sm:col-span-2 lg:col-span-4">
      <AoikumoSyncBadge :custom-metadata="custom_metadata" />
    </div>
  </div>
</template>
